import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Block, Input, Typography } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';

import { readOnlyFieldTypographyProps } from '../../../components/PropertyGrid/fields/consts.ts';
import {
  PropertyGridForm,
  PropertyGridSectionProps,
} from '../../../components/PropertyGrid/types.ts';
import { handleUpdatePropertyData } from '../../../components/PropertyGrid/utils.ts';
import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';

const DataSourceField = ({
  columnIndex,
  isArv = false,
}: {
  columnIndex: PropertyGridSectionProps['columnIndex'];
  isArv?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { setValue, watch } = useFormContext<PropertyGridForm>();

  const arvFieldPrefix = isArv ? 'arv.' : '';
  const fieldName: FieldPath<PropertyGridForm> = `columns.${columnIndex}.propertyData.editableData.${arvFieldPrefix}improvements.featureGridDataSources`;
  const value = watch(fieldName);

  const [displayValue, setDisplayValue] = useState<string | undefined>(value);

  const propertyData = watch(`columns.${columnIndex}.propertyData`);
  const propertyId = watch(`columns.${columnIndex}.propertyId`);

  useEffect(() => {
    setDisplayValue(value);
  }, [value, setDisplayValue]);

  const handleSetFieldValue = (newValue?: string) => {
    setValue(fieldName, newValue);
    if (propertyData) handleUpdatePropertyData(propertyData, propertyId, watch(), dispatch);
  };

  return (
    <Input
      type="text"
      __dangerDoNotUseSx={{
        width: '100%',
        height: '32px',
        padding: '4px 8px',
        border: 'none',
        borderRadius: '0',
        fontSize: '14px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textWrap: 'nowrap',
        whiteSpace: 'nowrap',
      }}
      value={displayValue}
      onChange={setDisplayValue}
      onClear={() => setDisplayValue(undefined)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDisplayValue(value);
          if (e.currentTarget instanceof HTMLInputElement) e.currentTarget.blur();
        } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
          handleSetFieldValue(displayValue);
          if (e.currentTarget instanceof HTMLInputElement) e.currentTarget.blur();
        }
      }}
    />
  );
};

export const FeatureGridDataSourcesSection = ({
  columnIndex,
  fieldTypes,
}: PropertyGridSectionProps) => {
  const { hasArvOption } = useAppSelector(selectOrder);

  const { watch } = useFormContext<PropertyGridForm>();

  const columnType = watch(`columns.${columnIndex}.type`);
  const propertyId = watch(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  if (columnType !== 'property') {
    return (
      <Block>
        <Typography {...readOnlyFieldTypographyProps} />
      </Block>
    );
  }

  // TODO - AV-169
  const showAivComps = true;
  const showArvComps = true;

  const showLabels = fieldTypes.includes('label');

  return (
    <>
      {showLabels && <Typography {...readOnlyFieldTypographyProps}>Data Source(s)</Typography>}
      {(!isSubject || showAivComps) && <DataSourceField columnIndex={columnIndex} />}
      {isSubject && hasArvOption && showArvComps && (
        <DataSourceField columnIndex={columnIndex} isArv />
      )}
    </>
  );
};
